<!-- eslint-disable vue/no-v-html -->
<template>
    <div class="sl-order-form__line-side" :class="'sl-order-form__line-side-' + side">
        <!-- Helper -->
        <div class="sl-order-form__line-band-wrapper">
            <div class="sl-order-form__line-band-content"></div>
        </div>

        <!-- Error Wrapper -->
        <div class="sl-order-form__line-content-wrapper">
            <!-- Field Wrapper -->
            <div class="sl-order-form__field-wrapper">
                <!-- Field -->
                <div
                    class="sl-order-form__ckrb"
                    :class="{ 'sl-order-form__ckrb--loading': loading }"
                >
                    <div
                        v-if="$store.getters['account/isStaff']"
                        class="lsn-btn"
                        :class="{
                            'lsn-btn--primary-outline': !disabled,
                            'lsn-btn--gray-outline': disabled,
                            'cursor-not-allowed': disabled,
                        }"
                        :tabindex="tabIndex"
                        @click="onClick"
                        @keypress.enter="onClick"
                        @keypress.space.prevent="onClick"
                    >
                        <span class="sl-order-form__ckrb-label">
                            <template v-if="loading">
                                <img src="/img/loader.gif" class="inline mx-14" />
                            </template>

                            <template v-else>
                                {{ t('parameters.FILL_MISSING_K_READINGS_BUTTON.button_label') }}
                            </template>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Suffix -->
            <div v-if="missing" class="sl-order-form__suffix-wrapper">
                <div class="sl-order-form__suffix">
                    <div v-for="param in missing" :key="param.code">
                        {{ param.label }}
                        =
                        <span class="text-primary-500">
                            {{ pf(param.value, param.code) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Error -->
            <div v-if="error" class="sl-order-form__error-wrapper">
                <div class="sl-order-form__error">
                    {{ error }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { fieldProps, useFieldComposable } from '@/composables/FieldComposable';
import FormPrototype from '@/models/FormPrototype';
import { pf } from '@/utils/vue-formatter';


// ------------------------------------------------------------ PROPS

const props = defineProps(fieldProps);


// ------------------------------------------------------------ EMITS

const emit = defineEmits([]);


// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n({ useScope: 'global' });
const store = useStore();
const {
    // Computed
    tabIndex,
} = useFieldComposable(props, emit, store);


// ------------------------------------------------------------ DATA

const loading = ref(false);


// ------------------------------------------------------------ COMPUTED

const disabled = computed(() => loading.value);

const proto = computed(() =>
{
    /** @type {FormPrototype} */
    const proto = store.getters['lensOrder/getPrototype'](props.side);
    return proto;
});

const kFlat = computed(() => proto.value.getValue('EYE_CORNEA_RADIUS'));

const kDia = computed(() =>
{
    const d = Math.round(((kFlat.value - 7.35) * 3.1 + 10.4) * 10) / 10;
    return Math.max(Math.min(d, 12.8), 10.8);
});
const kSteep    = computed(() => Math.round((kFlat.value - 0.1) * 100) / 100);
const kEccFlat  = computed(() => rad2ecc(kFlat.value));
const kEccSteep = computed(() => rad2ecc(kSteep.value));

const missing = computed(() =>
{
    if(!kFlat.value)
    {
        return [];
    }

    const missing = [];
    const params = { EYE_CORNEA_DIAMETER: kDia.value };
    if(store.getters['account/isStaff'])
    {
        params.EYE_CORNEA_ECCENTRICITY   = kEccFlat.value;
        params.EYE_CORNEA_RADIUS_2       = kSteep.value;
        params.EYE_CORNEA_ECCENTRICITY_2 = kEccSteep.value;
        params.EYE_CORNEA_ANGLE          = 0;
    }

    for(const code in params)
    {
        const value = params[code];
        const cValue = proto.value.getValue(code);
        if(typeof cValue === 'undefined' || cValue === null || cValue === '')
        {
            const param = proto.value.getParameterByCode(code);
            const label = param?.form_label ?? t(`parameters.${code}.label`);
            missing.push({
                code,
                value,
                label,
            });
        }
    }

    return missing;
});


// ------------------------------------------------------------ METHODS

function onClick()
{
    if(loading.value)
        return;

    // Validate the input
    if(!kFlat.value)
    {
        props.parameter.setError(t('parameters.FILL_MISSING_K_READINGS_BUTTON.error.missing_input'));
        proto.value.getParameterByCode('EYE_CORNEA_RADIUS')?.setError(t('validation.required'));
        return;
    }

    // Set the missing K readings
    for(const { code, value } of missing.value)
    {
        proto.value.setValue(code, value, {
            form_display:          'HIDDEN',
            form_splitted_display: 'HIDDEN',
        });
    }

    // Overwrite the current fitting with a fresh template
    loading.value = true;
    store.dispatch('lensOrder/template', { side: props.side })
        .finally(() =>
        {
            loading.value = false;
        });
}

/**
 * Calculate the mean eccentricity for a corneal radius.
 *
 * @param {number} rad
 * @returns {number}
 */
function rad2ecc(rad)
{
    const e = Math.round(((rad * 0.1) - 0.28) * 100) / 100;
    return Math.max(Math.min(e, 0.7), 0.3);
}
</script>

<style scoped>
@reference "#main.css";

.sl-order-form__ckrb-label
{
    @apply flex justify-center items-center w-full h-full;
}
</style>
